import * as THREE from 'three';
import { BasketballDimensions } from '@shared/constants/BasketballDimensions';

/**
 * Utility class for visualizing debugging elements in the scene
 */
export class DebugTools {
  /**
   * Creates and returns a set of coordinate axes to help visualize the scene
   * X axis - Red
   * Y axis - Green
   * Z axis - Blue
   * @returns THREE.Group containing the axes
   */
  public static createCoordinateAxes(): THREE.Group {
    const axesSize = BasketballDimensions.debug.axesSize;
    const group = new THREE.Group();

    // Create X-axis (red)
    const xAxisGeometry = new THREE.CylinderGeometry(0.05, 0.05, axesSize, 8);
    xAxisGeometry.rotateZ(Math.PI / 2);
    const xAxisMaterial = new THREE.MeshBasicMaterial({ 
      color: BasketballDimensions.debug.axesColors.x,
      transparent: true,
      opacity: 0.75
    });
    const xAxis = new THREE.Mesh(xAxisGeometry, xAxisMaterial);
    xAxis.position.set(axesSize / 2, 0, 0);
    group.add(xAxis);

    // Add X-axis cone (arrow)
    const xConeMaterial = new THREE.MeshBasicMaterial({ 
      color: BasketballDimensions.debug.axesColors.x,
      transparent: true,
      opacity: 0.75
    });
    const xConeGeometry = new THREE.ConeGeometry(0.2, 0.5, 8);
    xConeGeometry.rotateZ(-Math.PI / 2);
    const xCone = new THREE.Mesh(xConeGeometry, xConeMaterial);
    xCone.position.set(axesSize, 0, 0);
    group.add(xCone);

    // Add X label
    this.addTextLabel('X', new THREE.Vector3(axesSize + 0.5, 0, 0), BasketballDimensions.debug.axesColors.x, group);

    // Create Y-axis (green)
    const yAxisGeometry = new THREE.CylinderGeometry(0.05, 0.05, axesSize, 8);
    const yAxisMaterial = new THREE.MeshBasicMaterial({ 
      color: BasketballDimensions.debug.axesColors.y,
      transparent: true,
      opacity: 0.75
    });
    const yAxis = new THREE.Mesh(yAxisGeometry, yAxisMaterial);
    yAxis.position.set(0, axesSize / 2, 0);
    group.add(yAxis);

    // Add Y-axis cone (arrow)
    const yConeMaterial = new THREE.MeshBasicMaterial({ 
      color: BasketballDimensions.debug.axesColors.y,
      transparent: true,
      opacity: 0.75
    });
    const yConeGeometry = new THREE.ConeGeometry(0.2, 0.5, 8);
    const yCone = new THREE.Mesh(yConeGeometry, yConeMaterial);
    yCone.position.set(0, axesSize, 0);
    group.add(yCone);

    // Add Y label
    this.addTextLabel('Y', new THREE.Vector3(0, axesSize + 0.5, 0), BasketballDimensions.debug.axesColors.y, group);

    // Create Z-axis (blue)
    const zAxisGeometry = new THREE.CylinderGeometry(0.05, 0.05, axesSize, 8);
    zAxisGeometry.rotateX(Math.PI / 2);
    const zAxisMaterial = new THREE.MeshBasicMaterial({ 
      color: BasketballDimensions.debug.axesColors.z,
      transparent: true,
      opacity: 0.75
    });
    const zAxis = new THREE.Mesh(zAxisGeometry, zAxisMaterial);
    zAxis.position.set(0, 0, axesSize / 2);
    group.add(zAxis);

    // Add Z-axis cone (arrow)
    const zConeMaterial = new THREE.MeshBasicMaterial({ 
      color: BasketballDimensions.debug.axesColors.z,
      transparent: true,
      opacity: 0.75
    });
    const zConeGeometry = new THREE.ConeGeometry(0.2, 0.5, 8);
    zConeGeometry.rotateX(Math.PI / 2);
    const zCone = new THREE.Mesh(zConeGeometry, zConeMaterial);
    zCone.position.set(0, 0, axesSize);
    group.add(zCone);

    // Add Z label
    this.addTextLabel('Z', new THREE.Vector3(0, 0, axesSize + 0.5), BasketballDimensions.debug.axesColors.z, group);

    // Add origin marker
    const originGeometry = new THREE.SphereGeometry(0.2, 16, 16);
    const originMaterial = new THREE.MeshBasicMaterial({ 
      color: 0xffffff,
      transparent: true,
      opacity: 0.8
    });
    const origin = new THREE.Mesh(originGeometry, originMaterial);
    origin.position.set(0, 0, 0);
    group.add(origin);

    // Add origin label
    this.addTextLabel('O', new THREE.Vector3(0.3, 0.3, 0.3), 0xffffff, group);

    return group;
  }

  /**
   * Adds a text label at the specified position
   * @param text The text to display
   * @param position The position of the text
   * @param color The color of the text
   * @param parent The parent group to add the text to
   */
  private static addTextLabel(text: string, position: THREE.Vector3, color: number, parent: THREE.Group): void {
    const canvas = document.createElement('canvas');
    const context = canvas.getContext('2d');
    
    if (!context) return;

    canvas.width = 128;
    canvas.height = 128;
    
    context.fillStyle = '#000000';
    context.fillRect(0, 0, canvas.width, canvas.height);
    
    context.font = 'Bold 72px Arial';
    context.textAlign = 'center';
    context.textBaseline = 'middle';
    context.fillStyle = '#' + color.toString(16).padStart(6, '0');
    context.fillText(text, canvas.width / 2, canvas.height / 2);
    
    const texture = new THREE.CanvasTexture(canvas);
    const material = new THREE.SpriteMaterial({ 
      map: texture,
      transparent: true
    });
    
    const sprite = new THREE.Sprite(material);
    sprite.position.copy(position);
    sprite.scale.set(0.8, 0.8, 1);
    
    parent.add(sprite);
  }

  /**
   * Creates a grid helper for visualizing the ground plane
   * @param size The size of the grid
   * @param divisions The number of divisions in the grid
   * @returns THREE.GridHelper
   */
  public static createGridHelper(size: number = 30, divisions: number = 30): THREE.GridHelper {
    return new THREE.GridHelper(size, divisions, 0x888888, 0x444444);
  }
} 